import React, { useEffect, useState } from "react";
import axios from "axios";
import { PlusCircle, Pencil, Trash2 } from "lucide-react";
import Sidebar from "../Sidebar";
import { useNavigate } from "react-router-dom";
import StoryTypeForm from "./storyType";

const StoryTypeTable = () => {
  const navigate = useNavigate();
  const [storyTypes, setStoryTypes] = useState([]);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingStoryType, setEditingStoryType] = useState(null);
  const [activeSidebarItem, setActiveSidebarItem] = useState("story-type");

  const getDifficultyColor = (level) => {
    if (level <= 3) return "bg-green-100 text-green-800";
    if (level <= 6) return "bg-yellow-100 text-yellow-800";
    return "bg-red-100 text-red-800";
  };

  const getAllStoryTypes = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/get-story-types`,
        {
          withCredentials: true,
        },
      );
      setStoryTypes(response.data);
    } catch (err) {
      console.error("Error fetching story types:", err);
    }
  };

  const handleDeleteStoryType = async (id) => {
    if (window.confirm("Are you sure you want to delete this story type?")) {
      try {
        await axios.delete(
          `${process.env.REACT_APP_API_URL}/delete-story-type/${id}`,
          {
            withCredentials: true,
          },
        );
        await getAllStoryTypes();
      } catch (err) {
        console.error("Error deleting story type:", err);
      }
    }
  };
  const handleEditStoryType = (storyType) => {
    setEditingStoryType(storyType);
    setIsFormOpen(true);
  };

  const handleSidebarItemClick = (itemId, path) => {
    navigate(path);
    setActiveSidebarItem(itemId);
  };

  const handleFormSubmit = async (formData) => {
    try {
      if (editingStoryType) {
        await axios.put(
          `${process.env.REACT_APP_API_URL}/update-story-type/${editingStoryType.id}`,
          formData,
          { withCredentials: true },
        );
      } else {
        await axios.post(
          `${process.env.REACT_APP_API_URL}/create-story-type`,
          formData,
          { withCredentials: true },
        );
      }
      await getAllStoryTypes();
      setIsFormOpen(false);
      setEditingStoryType(null);
    } catch (err) {
      console.error("Error submitting story type:", err);
    }
  };

  useEffect(() => {
    getAllStoryTypes();
  }, []);

  return (
    <div className="min-h-screen bg-gray-50 flex">
      <Sidebar
        activeSidebarItem={activeSidebarItem}
        onSidebarItemClick={handleSidebarItemClick}
      />
      <main className="flex-1">
        <div className="bg-white rounded-lg shadow-md">
          <div className="p-6">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-semibold text-gray-900">
                Story Types
              </h2>
              <button
                onClick={() => {
                  setEditingStoryType(null);
                  setIsFormOpen(true);
                }}
                className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors duration-200"
              >
                <PlusCircle className="w-5 h-5 mr-2" />
                Add Story Type
              </button>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Name
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Duration Range
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Shorts Count
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {storyTypes.map((story) => (
                    <tr
                      key={story.id}
                      className="hover:bg-gray-50 transition-colors"
                    >
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {story.name}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                        {story.durationRange}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                        {story.shortEligibleCount}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
  <button
    onClick={() => handleEditStoryType(story)}
    className="text-blue-600 hover:text-blue-900 p-2 rounded-md hover:bg-blue-50 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
  >
    <Pencil className="w-5 h-5" />
  </button>
                        <button
                          onClick={() => handleDeleteStoryType(story._id)}
                          className="text-red-600 hover:text-red-900 p-2 rounded-md hover:bg-red-50 transition-colors focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
                        >
                          <svg
                            className="w-5 h-5"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth="2"
                              d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                            />
                          </svg>
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </main>

      {/* Modal */}
      {isFormOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <StoryTypeForm
            initialData={editingStoryType}
            onSubmit={handleFormSubmit}
            getAllStoryTypes={getAllStoryTypes}
            onCancel={() => {
              setIsFormOpen(false);
            }}
          />
        </div>
      )}
    </div>
  );
};

export default StoryTypeTable;
